import { Theme, makeStyles, createStyles, FormLabel } from '@material-ui/core';
import React from 'react'
import RoiBoostButton from '../common/Table/RoiBoostButton';
import { Button, Typography, Modal, Box, ListItem, List, Grid, Stack, Switch, FormControl, FormControlLabel } from '@mui/material';
import { StackConstraint } from './store/types';

const useStyles = makeStyles((theme: Theme) => createStyles({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    fontFamily: 'Roboto',
    '& .MuiButton-contained.Mui-disabled': {
      background: '#343434 !important',
    },
    '& .subtitle': {
      fontSize: 14,
      // lineHeight: '12px',
      fontWeight: 400,
      padding: '12px 8px',
    },
    '& .header': {
      backgroundColor: 'rgba(0, 118, 54, 0.15)',
      padding: 0,
      '& .headerCell': {
        textAlign: 'center',
        fontSize: 12,
        padding: '8px 8px',
      }
    },
    '& .row': {
      padding: 0,
      fontWeight: 500,
      '& .cell': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 700,
        padding: '8px 6px',
      }
    },
    '& .total': {
      padding: '0',
      fontWeight: 500,
      '& .cell': {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 6px',
      },
      '& .sum': {
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: 14,
        lineHeight: '150%',
        color: '#31FF39',
      }
    },
    '& .unstacked': {
      padding: '0',
      fontWeight: 500,
      '& .cell': {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 6px',
      },
      '& .sum': {
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: 14,
        lineHeight: '150%',
        color: '#31FF39',
      }
    },
    '& .input': {
      color: 'white',
      width: '100%',
      border: '1px solid white',
      borderRadius: 6,
      padding: '2px 12px'
    }
  },
  modalContent: {
    backgroundColor: '#0A0A0A',
    borderRadius: 10,
    minHeight: 390,
    width: 260,
    overflow: 'hidden',
  },
  list: {
    padding: 0,
  },
  button: {
    backgroundColor: "#015ACE",
    borderRadius: 4,
    width: 85,
    height: 26,
    padding: '0 12px',
    color: 'white',
    '&:hover': {
      backgroundColor: "#015ACE",
    },
  },
  cancelButton: {
    color: 'white',
    marginRight: 2,
    '&:hover': {
    },
  },
  toggleGroup: {
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
    '& .MuiToggleButton-root': {
      color: theme.palette.common.white,
    },
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: '#003A85',
    }
  },
})
);

interface OwnProps {
  readonly constraints: StackConstraint[];
  readonly runback: StackConstraint;
  readonly defaultConstraints?: StackConstraint[];
  readonly defaultRunback?: StackConstraint;
  readonly onApply: (newConstraints: StackConstraint[], runback: StackConstraint, useCustomExposure: boolean) => void;
  readonly sport: string;
  readonly savedStackConstraintKey: string;
  readonly useCustomExposures: boolean;
}

type Props = OwnProps;

const StackConstraints = (props: Props) => {
  const { constraints, runback, defaultConstraints, defaultRunback, sport, savedStackConstraintKey, useCustomExposures, onApply } = props;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [internalConstraints, setInternalConstraints] = React.useState([] as StackConstraint[]);
  const [internalRunback, setInternalRunback] = React.useState({} as StackConstraint);
  const [totalPercent, setTotalPercent] = React.useState(0);
  const [customExposure, setCustomExposure] = React.useState(false);

  const handleApply = () => {
    onApply(internalConstraints, internalRunback, customExposure);
    localStorage.setItem(savedStackConstraintKey, JSON.stringify({constraints: internalConstraints, runback: internalRunback}));
    setOpen(false);
  };

  const handleRestoreDefaults = () => {
    if (defaultConstraints) setInternalConstraints(defaultConstraints);
    if (defaultRunback) setInternalRunback(defaultRunback);
  };

  const handlePercentChange = (constraint: StackConstraint) => {
    const previousConstraints = [...internalConstraints];
    const index = previousConstraints.findIndex(x => x.primary === constraint.primary && x.secondary === constraint.secondary);

    previousConstraints[index] = constraint;

    setInternalConstraints(previousConstraints);
  }

  const handleRunbackPercentChange = (constraint: StackConstraint) => {
    const newRunback = {
      ...runback,
      percent: constraint.percent
    }

    setInternalRunback(newRunback);
  };

  React.useEffect(() => {
    setInternalConstraints(constraints);
    setInternalRunback(runback);
    setCustomExposure(useCustomExposures);
  }, [constraints, runback, useCustomExposures])
  React.useEffect(() => {
    if (internalConstraints) {
      const total = internalConstraints.reduce((partialSum, a) => partialSum + a.percent * 100, 0);
      setTotalPercent(total);
    }
  }, [internalConstraints])

  return (
    <div>
      <Box>
        <Button
          sx={{
            width: 200,
            height: 36,
            borderRadius: '6px !important',
            fontFamily: 'Inter !important',
            fontSize: '12px !important',
            fontStyle: 'normal !important',
            fontHeight: 500,
            backgroundColor: '#000A16 !important',
            fontWeight: 400,
            display: 'flex !important',
            justifyContent: 'space-between !important',
            color: 'white !important',
            textTransform: 'capitalize'
          }}
          onClick={() => setOpen(true)}
        >
          Stack Type Exposures
          <StackSVG />
        </Button>
      </Box>
      <Modal open={open} className={classes.modal} disableAutoFocus onClose={(event: object, reason: string) => {
        if (reason?.toLowerCase() === 'backdropclick') {
          setOpen(false)
        }
      }}>
        <div className={classes.modalContent}>
          <Box padding="10px 12px">
            <Typography sx={{ color: 'FFF !important', fontSize: '14px', fontWeight: 700, lineHeight: '140%' }}>
              Stack Type Exposures
            </Typography>
            <Typography sx={{ color: 'FFF', fontSize: '10px', fontWeight: 400, lineHeight: '120%' }}>
              Adjust stack exposures and ensure the Desired Exposure is less than or equal to 100%
            </Typography>
          </Box>
          <Box>
            <List className={classes.list}>
              <ListItem className='header'>
                <Grid container>
                  <Grid item xs={6} className='headerCell'>Stack Type</Grid>
                  <Grid item xs={6} className='headerCell'>Desired Exposure</Grid>
                </Grid>
              </ListItem>
            </List>
            <List className={classes.list}>
              {
                internalConstraints.map((x: StackConstraint, i: number) => (
                  <ConstraintListItem onIncrement={handlePercentChange} constraint={x} totalPercent={totalPercent} key={x.primary} sport={sport} />
                ))
              }
              {sport?.toLowerCase() === 'mlb' && (
                <ListItem className='unstacked'>
                  <Grid container>
                    <Grid item xs={6} className='cell'>Unstacked</Grid>
                    <Grid item xs={6} className='cell sum' style={{ color: totalPercent > 100 ? '#FF6A7B' : '', }}>{(100 - totalPercent)?.toFixed(0)}%</Grid>
                  </Grid>
                </ListItem>
              )}
              <ListItem className='total'>
                <Grid container>
                  <Grid item xs={6} className='cell'>Total</Grid>
                  <Grid item xs={6} className='cell sum' style={{ color: totalPercent > 100 ? '#FF6A7B' : '', }}>{totalPercent?.toFixed(0)}%</Grid>
                </Grid>
              </ListItem>
            </List>
          </Box>
          {
            sport?.toLowerCase() !== 'mlb' && (
              <Box className='total' marginTop="20px">
                <ConstraintListItem text="Includes Opposing Player" constraint={internalRunback} onIncrement={handleRunbackPercentChange} totalPercent={internalRunback?.percent * 100} sport={sport} />
              </Box>
            )
          }
          <Box padding="10px 12px" bgcolor='#0A0A0A' display="flex" justifyContent={defaultConstraints ? "space-between" : "flex-end"} alignItems={'center'} gap={1}>
            {defaultConstraints && (
              <Button
                size='small'
                onClick={handleRestoreDefaults}
                sx={{
                  color: '#738DF8 !important',
                  fontSize: '11px !important',
                  fontWeight: 400,
                  textTransform: 'none',
                  padding: '0 !important',
                  minWidth: 'auto !important',
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                    textDecoration: 'underline',
                  },
                }}
              >
                Restore Defaults
              </Button>
            )}
            <Button variant='contained' className={classes.button} size='small' onClick={() => handleApply()} disabled={totalPercent > 100}>Apply</Button>
          </Box>
        </div>
      </Modal>
    </div>
  )
}

const ConstraintListItem = (props: { text?: string, constraint: StackConstraint, onIncrement: (constraint: StackConstraint) => void, totalPercent: number, sport: string }) => {
  const { constraint, totalPercent, text, sport, onIncrement } = props;
  const { primary, secondary, percent } = constraint;


  const getTertiaryValue = () => {
    const sum = (primary as number) + secondary;

    if (sum === 8 || secondary === 0) {
      return '';
    }
    if (sum === 7) {
      return '-1';
    }
    return '-x'
  }

  return (
    <ListItem className='row'>
      <Grid container>
        {
          sport?.toLowerCase() === 'mlb' ? (
            <>
              <Grid item xs={6} className='cell'>{`${primary}-${secondary === 0 ? 'x' : secondary}${getTertiaryValue()}`}</Grid>
            </>
          ) : (
            <>
              <Grid item xs={6} className='cell'>{text ? text : `QB + ${primary}`}</Grid>
            </>
          )
        }
        <Grid item xs={6} className='cell total'><RoiBoostButton defaultPercent={percent * 100} disableMax={!(totalPercent < 100) || percent >= 100} disableMin={!(totalPercent > 0) || percent <= 0} onChange={(newPercent) => onIncrement({ primary, secondary, percent: newPercent / 100 })} previousPercent={parseInt((percent * 100).toFixed(0))} /></Grid>
      </Grid>
    </ListItem>
  );
}

const StackSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M1.5 18C1.1 18 0.75 17.85 0.45 17.55C0.15 17.25 0 16.9 0 16.5V1.5C0 1.1 0.15 0.75 0.45 0.45C0.75 0.15 1.1 0 1.5 0H16.5C16.9 0 17.25 0.15 17.55 0.45C17.85 0.75 18 1.1 18 1.5V16.5C18 16.9 17.85 17.25 17.55 17.55C17.25 17.85 16.9 18 16.5 18H1.5ZM1.5 16.5H16.5V1.5L1.5 16.5ZM11.85 15.3V13.1H9.65V11.85H11.85V9.65H13.1V11.85H15.3V13.1H13.1V15.3H11.85ZM2.7 5.05H7.75V3.8H2.7V5.05Z" fill="white" fillOpacity="0.56" />
  </svg>
)

export default StackConstraints